/**
 * Switcher component
 */
export default class {
  /**
   * Container
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #element;

  /**
   * Switcher options
   *
   * @private
   *
   * @type {Object<string, HTMLElement[]>}
   */
  #options;

  /**
   * Current selected option
   *
   * @private
   *
   * @type {string}
   */
  #currentOption = '';

  /**
   * Lock component state
   *
   * @private
   *
   * @type {string}
   */
  #lock = false;

  /**
   * @param {HTMLElement} element Component element
   */
  constructor(element) {
    this.#element = element;
    if (!this.#element) {
      throw new Error('Licorn Switcher: element is required');
    }

    this.#init();
  }

  /**
   * Initialize listeners and default state
   *
   * @private
   *
   * @return {void}
   */
  #init() {
    this.#options = {};
    this.#element
      .querySelectorAll('.lcas-switcher-option')
      .forEach((option) => {
        const control = option.querySelector('input[type="radio"]');
        if (!control) {
          return;
        }

        // input listener
        control.addEventListener('change', (e) =>
          this.select(e.currentTarget.value)
        );

        // find selected option
        if (!this.#currentOption && option.classList.contains('active')) {
          this.#currentOption = control.value;
        }

        this.#options[control.value] = {
          element: option,
          value: control.value,
          input: control,
        };
      });

    // default option
    if (!this.#currentOption) {
      this.select(Object.keys(this.#options)[0]);
    }
  }

  /**
   * Send a custom event to the root element
   *
   * @private
   *
   * @param {string} event Event name
   * @param {mixed} data Event data
   *
   * @return {void}
   */
  #dispatchEvent(event, data = null) {
    this.#element.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  /**
   * Lock component state
   *
   * @return {void}
   */
  lock() {
    Object.values(this.#options).forEach((option) => {
      if (option.value === this.#currentOption) {
        return;
      }

      option.element.classList.add('hidden');
    });

    this.#lock = true;
  }

  /**
   * Unlock component state
   *
   * @return {void}
   */
  unlock() {
    Object.values(this.#options).forEach((option) =>
      option.element.classList.remove('hidden')
    );

    this.#lock = false;
  }

  /**
   * Add a listener to the root element
   *
   * @param {string} event Event name
   * @param {EventListener|Function} listener Event listener or callback function
   * @param {Object|null} options Listener options or useCapture parameter
   *
   * @return {void}
   */
  on(event, listener, options = null) {
    this.#element.addEventListener(event, listener, options);
  }

  /**
   * Remove a listener from the root element
   *
   * @param {string} event Event name
   * @param {EventListener|Function} listener Event listener or callback function
   * @param {Object|null} options Listener options or useCapture parameter
   *
   * @return {void}
   */
  off(event, listener, options = null) {
    this.#element.removeEventListener(event, listener, options);
  }

  /**
   * Select an option
   *
   * @param {string} option Selected option
   * @param {boolean} lock Lock state
   *
   * @return {void}
   */
  select(option, lock = false) {
    // validate option
    if (
      this.#lock ||
      !this.#options[option] ||
      this.#currentOption === option
    ) {
      return;
    }

    // unselect
    if (this.#currentOption) {
      const prev = this.#options[this.#currentOption];
      prev.input.checked = false;
      prev.element.classList.remove('active');
    }

    // select
    this.#currentOption = option;
    const current = this.#options[this.#currentOption];
    current.input.checked = true;
    current.element.classList.add('active');

    // lock
    if (lock) {
      this.lock();
    }

    // send event
    this.#dispatchEvent('switcher_change', { option: this.#currentOption });
  }

  /**
   * Get the value of the selected option
   *
   * @return {string}
   */
  value() {
    return this.#currentOption;
  }
}
